import { type UserDetail } from './TeoApi';
import type { AnalysisJSON } from './text/AnalysisResult';
import type { GeneratorUiHandlerStoredState } from './text/generator/GeneratorUiHandler';
import type { HistoryJSON } from './text/history/HistoryManager';
import { type AnalysisConfiguration } from './text/TeoText';
import type { TeoTextStoredState } from './text/TeoText.store';
import type { TextUiState } from './text/TextUi';

export type BaseTextData = {
  id: number;
  text: string;
  lastEdited?: number;
  serialized?: string;
  analysisConfiguration?: Partial<AnalysisConfiguration> | undefined;
  uiState?: Partial<TextUiState>;
  storeState?: TeoTextStoredState;
  analysis?: AnalysisJSON; // used in core -> move to text
  textSessionId?: string | null;
};
export type TextDataAnalysis = {
  history?: HistoryJSON; // used in core -> move to text
};
export type TextDataGenerator = {
  generatorUi?: GeneratorUiHandlerStoredState;
};

export type TextData = BaseTextData & (TextDataAnalysis | TextDataGenerator);

export class TeoStorage {
  private dbName: string;
  private dbVersion: number = 14;
  private db: IDBDatabase | null = null;
  private dbReady: Promise<void>;

  constructor(dbName: string = 'TeoDB') {
    this.dbName = dbName;
    this.dbReady = this.initDB();
  }

  private readonly LAST_USED_KEY = 'lastUsed';
  private readonly ACTIVE_TEXT_KEY = 'activeTextId';
  private readonly MAX_DURATION = 10 * 60 * 60 * 1000; // 10 hours

  private readonly migrations = new Map<number, () => void>([]);

  private initDB() {
    return new Promise<void>((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onupgradeneeded = (event) => {
        this.db = (event.target as IDBOpenDBRequest).result;
        for (let version = event.oldVersion + 1; version <= this.dbVersion; version++) {
          const migration = this.migrations.get(version);
          const nextMigration =
            version === this.dbVersion ? () => {} : this.migrations.get(version + 1);
          // if there is no migration for the current version, and the next version also has no migration, skip delete to the next one.
          if (!migration && !nextMigration) {
            continue;
          }
          if (migration) {
            migration();
          } else {
            // if there is no migration for the current version, reset everything
            this.reset();
          }
        }
      };

      request.onsuccess = (event) => {
        this.db = (event.target as IDBOpenDBRequest).result;

        this.checkAndHandleStaleStorage()
          .catch((err) => {
            console.warn('TeoStorage: failed to check last used timestamp', err);
          })
          .finally(() => {
            resolve();
          });
      };

      request.onerror = (event) => {
        console.error('IndexedDB error:', (event.target as IDBOpenDBRequest).error);
        reject((event.target as IDBOpenDBRequest).error);
      };
    });
  }

  /**
   * Checks the last used time and clears storage if it's older than MAX_DURATION.
   * After check, sets the "last used" to current time.
   */
  private async checkAndHandleStaleStorage() {
    if (!this.db) throw new Error('Database not initialized');

    const now = Date.now();
    let lastUsed: number | null = null;

    // Read lastUsed from store
    await new Promise<void>((resolve) => {
      const tx = this.db!.transaction('settings', 'readonly');
      const store = tx.objectStore('settings');
      const req = store.get(this.LAST_USED_KEY);
      req.onsuccess = () => {
        lastUsed = req.result ? req.result.data : null;
        resolve();
      };
      req.onerror = () => resolve();
    });

    if (lastUsed !== null && now - lastUsed > this.MAX_DURATION) {
      await this.clearEverything();
    }

    await this.updateLastUsed(now);
  }

  private async updateLastUsed(date?: number) {
    if (!this.db) return;
    const tx = this.db.transaction('settings', 'readwrite');
    const store = tx.objectStore('settings');
    store.put({ id: this.LAST_USED_KEY, data: date ?? Date.now() });
  }

  private async getObjectStore(
    storeName: string,
    mode: IDBTransactionMode,
  ): Promise<IDBObjectStore> {
    await this.dbReady;
    if (!this.db) {
      throw new Error('Database not initialized');
    }
    // Update last used each access (except for the check in init, which is done early)
    this.updateLastUsed();
    const transaction = this.db.transaction(storeName, mode);
    return transaction.objectStore(storeName);
  }

  async saveUser(user: UserDetail) {
    const store = await this.getObjectStore('settings', 'readwrite');
    const request = store.put({ id: 'userData', data: user });
    return this.handleRequest(request);
  }

  async loadUser(): Promise<UserDetail | null> {
    const store = await this.getObjectStore('settings', 'readonly');
    const request = store.get('userData');
    const result = await this.handleRequest<{ id: number; data: UserDetail }>(request);
    return result ? result.data : null;
  }

  async clearUser() {
    const store = await this.getObjectStore('settings', 'readwrite');
    const request = store.delete('userData');
    return this.handleRequest(request);
  }

  async saveActiveTextId(id: number) {
    const store = await this.getObjectStore('settings', 'readwrite');
    const request = store.put({ id: this.ACTIVE_TEXT_KEY, data: id });
    return this.handleRequest(request);
  }

  async loadActiveTextId(): Promise<number | null> {
    const store = await this.getObjectStore('settings', 'readonly');
    const request = store.get(this.ACTIVE_TEXT_KEY);
    const result = await this.handleRequest<{ id: string; data: number } | undefined>(request);
    return typeof result?.data === 'number' ? result.data : null;
  }

  async saveText(textData: TextData) {
    const data = textData;
    const store = await this.getObjectStore('texts', 'readwrite');
    try {
      const request = store.put({ id: data.id, data });
      return await this.handleRequest(request);
    } catch (error) {
      console.error('TeoStorage: failed to save text', error);
      console.info('TeoStorage: text data', data);
      throw error;
    }
  }

  async loadTexts(): Promise<TextData[] | null> {
    const store = await this.getObjectStore('texts', 'readonly');
    const request = store.getAll();
    const result: { id: number; data: TextData }[] =
      await this.handleRequest<{ id: number; data: TextData }[]>(request);

    return result.map((data) => data.data as TextData) || null;
  }

  async deleteText(id: number) {
    const store = await this.getObjectStore('texts', 'readwrite');
    const request = store.delete(id);
    return this.handleRequest(request);
  }

  async clearEverything() {
    if (!this.db) throw new Error('Database not initialized');
    const transaction = this.db.transaction(['settings', 'texts'], 'readwrite');
    const settingsStore = transaction.objectStore('settings');
    const textsStore = transaction.objectStore('texts');
    settingsStore.clear();
    textsStore.clear();
    await new Promise<void>((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }

  private reset() {
    console.info('Reset app data!');
    if (!this.db) throw new Error('Database not initialized');
    for (const table of this.db.objectStoreNames) {
      this.db.deleteObjectStore(table);
    }

    this.db.createObjectStore('settings', { keyPath: 'id' });
    this.db.createObjectStore('texts', { keyPath: 'id' });
  }

  private handleRequest<T>(request: IDBRequest): Promise<T> {
    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }
}
